import { Theme, createTheme } from "@mui/material/styles";
import { FontWeight, ICustomerPortalTheme } from "@smartrr/shared/entities/CustomerPortalTheme";
import { mergeThemeWithDefault } from "@smartrr/shared/themes";
import { useMemo } from "react";

import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

const BODY_CLAMP_MIN_VAL = "9px";
const BODY_CLAMP_DYNAMIC_VAL = "2.75vw";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    mdsm: true; // adds the `mobile` breakpoint
  }
  interface Theme extends ICustomerPortalTheme {
    breakPoints?: BreakpointOverrides;
  }

  interface TypographyVariants {
    caption1: React.CSSProperties;
    caption2: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption1?: React.CSSProperties;
    caption2?: React.CSSProperties;
  }
}

export const getTheme = (customerTheme: ICustomerPortalTheme) => {
  const theme = createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
          mdsm: 700,
        },
      },
      typography: {
        // Only supporting variants outlined in designs
        subtitle1: undefined,
        subtitle2: undefined,
        caption: undefined,
        overline: undefined,
        caption1: undefined,
        caption2: undefined,
      },
    },
    // filling in missing values with defaults
    mergeThemeWithDefault(customerTheme)
  ) as Theme & ICustomerPortalTheme;

  /* X-LARGE HEADING */
  theme.typography.h1 = {
    fontFamily: theme.modernExtraLargeFontFamily,
    fontSize: theme.modernExtraLargeFontSize + "px",
    fontWeight: theme.modernExtraLargeFontWeight,
    lineHeight: theme.modernExtraLargeFontLineHeight + "px",
    letterSpacing: theme.modernExtraLargeFontLetterSpacing + "px",
    color: theme.modernExtraLargeFontColor,
  };

  /* LARGE HEADING */
  theme.typography.h2 = {
    fontFamily: theme.modernLargeFontFamily,
    fontSize: theme.modernLargeFontSize + "px",
    fontWeight: theme.modernLargeFontWeight,
    lineHeight: theme.modernLargeFontLineHeight + "px",
    letterSpacing: theme.modernLargeFontLetterSpacing + "px",
    color: theme.modernLargeFontColor,

    textTransform: "capitalize",
    marginBottom: "20px",
  };

  /* MEDIUM HEADING */
  theme.typography.h3 = {
    fontFamily: theme.modernMediumFontFamily,
    fontSize: theme.modernMediumFontSize + "px",
    fontWeight: theme.modernMediumFontWeight,
    lineHeight: theme.modernMediumFontLineHeight + "px",
    letterSpacing: theme.modernMediumFontLetterSpacing + "px",
    color: theme.modernMediumFontColor,

    marginTop: "0px",
    marginBottom: "15px",
  };

  /* SMALL HEADING */
  theme.typography.h4 = {
    fontFamily: theme.modernSmallFontFamily,
    fontSize: theme.modernSmallFontSize + "px",
    fontWeight: theme.modernSmallFontWeight,
    lineHeight: theme.modernSmallFontLineHeight + "px",
    letterSpacing: theme.modernSmallFontLetterSpacing + "px",
    color: theme.modernSmallFontColor,

    marginTop: "0px",
    marginBottom: "15px",
  };

  /* SUBHEADING */
  theme.typography.h5 = {
    fontFamily: theme.modernSubHeadingFontFamily,
    fontSize: theme.modernSubHeadingFontSize + "px",
    fontWeight: theme.modernSubHeadingFontWeight,
    lineHeight: theme.modernSubHeadingFontLineHeight + "px",
    letterSpacing: theme.modernSubHeadingFontLetterSpacing + "px",
    color: theme.modernSubHeadingFontColor,

    textTransform: "uppercase",
    marginTop: "0px",
  };

  /* BODY - Variant 1*/
  theme.typography.body1 = {
    fontFamily: theme.modernBodyFontFamily,
    fontSize: getBodyFontSizing(theme),
    fontWeight: theme.modernBodyFontWeight,
    lineHeight: theme.modernBodyFontLineHeight + "px",
    letterSpacing: theme.modernBodyFontLetterSpacing + "px",
    color: theme.modernBodyFontColor,

    margin: "0px",
  };

  /* BODY - Variant 2*/
  theme.typography.body2 = {
    fontFamily: theme.modernBodyFontFamily,
    fontSize: getBodyFontSizing(theme),
    fontWeight: 700,
    lineHeight: theme.modernBodyFontLineHeight + "px",
    letterSpacing: theme.modernBodyFontLetterSpacing + "px",
    color: theme.modernBodyFontColor,

    margin: "0px",
  };

  /* CAPTION - Variant 1 */
  theme.typography.caption1 = {
    fontFamily: theme.modernCaptionFontFamily,
    fontSize: theme.modernCaptionFontSize + "px",
    fontWeight: (theme.modernCaptionFontWeight as FontWeight) || 500,
    lineHeight: theme.modernCaptionFontLineHeight + "px",
    letterSpacing: theme.modernCaptionFontLetterSpacing + "px",
    color: theme.modernCaptionFontColor,

    width: "max-content",
    margin: "0px",
  };

  /* CAPTION - Variant 2 */
  theme.typography.caption2 = {
    fontFamily: theme.modernCaptionFontFamily,
    fontSize: theme.modernCaptionFontSize + "px",
    fontWeight: 700,
    lineHeight: theme.modernCaptionFontLineHeight + "px",
    letterSpacing: theme.modernCaptionFontLetterSpacing + "px",
    color: theme.modernCaptionFontColor,

    width: "max-content",
    margin: "0px",
  };

  return theme;
};

const getBodyFontSizing = (theme: Theme & ICustomerPortalTheme) => {
  if (theme.modernThemeSettings?.fontSizingType && theme.modernThemeSettings?.fontSizingType === "responsive") {
    return `clamp(${BODY_CLAMP_MIN_VAL},${BODY_CLAMP_DYNAMIC_VAL},${theme.modernBodyFontSize + "px"})`;
  }
  return theme.modernBodyFontSize + "px";
};

export const useSmartrrTheme = () => {
  const customerTheme = useSmartrrCustomerSelector(state => state.theme.theme);

  return useMemo(() => getTheme(customerTheme), []);
};
